import { useState } from 'react'
import AdminLayout from '../components/AdminLayout'
import { Image, Upload, Trash2, Eye, Download } from 'lucide-react'

const AdminCatalogImages = () => {
  const [images, setImages] = useState([])
  const [isUploading, setIsUploading] = useState(false)

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files)
    if (files.length === 0) return

    setIsUploading(true)
    
    // Simulation d'upload - à remplacer par l'API réelle
    setTimeout(() => {
      const newImages = files.map((file, index) => ({
        id: Date.now() + index,
        name: file.name,
        size: file.size,
        type: file.type,
        url: URL.createObjectURL(file),
        uploadedAt: new Date().toISOString(),
        productId: null,
        categoryId: null
      }))
      
      setImages(prev => [...prev, ...newImages])
      setIsUploading(false)
    }, 1000)
  }

  const handleDeleteImage = (imageId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette image ?')) {
      setImages(prev => prev.filter(img => img.id !== imageId))
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <AdminLayout title="Administration" subtitle="Images du catalogue">
      <div className="space-y-6">
        {/* En-tête avec upload */}
        <div className="card">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Gestion des images du catalogue</h2>
              <p className="text-sm text-gray-600">Téléchargez et gérez les images des produits du catalogue</p>
            </div>
            <div className="flex items-center space-x-3">
              <label className="btn-primary cursor-pointer">
                <Upload className="h-4 w-4 mr-2" />
                Télécharger des images
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={isUploading}
                />
              </label>
            </div>
          </div>

          {isUploading && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
                <span className="text-blue-700">Téléchargement en cours...</span>
              </div>
            </div>
          )}
        </div>

        {/* Liste des images */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Images du catalogue</h3>
          
          {images.length === 0 ? (
            <div className="text-center py-12">
              <Image className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">Aucune image</h4>
              <p className="text-gray-600 mb-4">
                Commencez par télécharger des images pour votre catalogue
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {images.map((image) => (
                <div key={image.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                  <div className="aspect-square bg-gray-100 relative">
                    <img
                      src={image.url}
                      alt={image.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 right-2 flex space-x-1">
                      <button
                        onClick={() => window.open(image.url, '_blank')}
                        className="p-1 bg-white rounded-full shadow-sm hover:bg-gray-50"
                        title="Voir l'image"
                      >
                        <Eye className="h-3 w-3 text-gray-600" />
                      </button>
                      <button
                        onClick={() => handleDeleteImage(image.id)}
                        className="p-1 bg-white rounded-full shadow-sm hover:bg-red-50"
                        title="Supprimer"
                      >
                        <Trash2 className="h-3 w-3 text-red-600" />
                      </button>
                    </div>
                  </div>
                  <div className="p-3">
                    <h4 className="font-medium text-gray-900 text-sm truncate" title={image.name}>
                      {image.name}
                    </h4>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatFileSize(image.size)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(image.uploadedAt).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Statistiques */}
        {images.length > 0 && (
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Statistiques</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center">
                  <Image className="h-8 w-8 text-orange-500 mr-3" />
                  <div>
                    <p className="text-2xl font-semibold text-gray-900">{images.length}</p>
                    <p className="text-sm text-gray-600">Images totales</p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center">
                  <Download className="h-8 w-8 text-blue-500 mr-3" />
                  <div>
                    <p className="text-2xl font-semibold text-gray-900">
                      {formatFileSize(images.reduce((total, img) => total + img.size, 0))}
                    </p>
                    <p className="text-sm text-gray-600">Taille totale</p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center">
                  <Upload className="h-8 w-8 text-green-500 mr-3" />
                  <div>
                    <p className="text-2xl font-semibold text-gray-900">
                      {images.filter(img => {
                        const today = new Date()
                        const uploadDate = new Date(img.uploadedAt)
                        return uploadDate.toDateString() === today.toDateString()
                      }).length}
                    </p>
                    <p className="text-sm text-gray-600">Ajoutées aujourd'hui</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}

export default AdminCatalogImages
